import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getQrCodeImage } from '../api/qrCodes';
import type { QrCode, QrImageFormat } from '../api/types';
import { DEFAULT_IMAGE_STYLE, type ImageStyle } from './ImageStyleFields';
import { QrArtwork } from './QrArtwork';

export interface ImageBatchChoice {
  format: QrImageFormat;
  style: ImageStyle;
}

export const DEFAULT_BATCH_CHOICE: ImageBatchChoice = { format: 'png', style: DEFAULT_IMAGE_STYLE };

const FORMATS: QrImageFormat[] = ['png', 'svg'];

const PREVIEW_SIZE = 10;

function usePreviewImage(qrCode: QrCode | undefined, { format, style }: ImageBatchChoice) {
  const [image, setImage] = useState<{ url: string | null; failed: boolean }>({ url: null, failed: false });

  useEffect(() => {
    if (!qrCode) {
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    setImage({ url: null, failed: false });

    getQrCodeImage(qrCode.id, format, PREVIEW_SIZE, style.foreground, style.background, style.logoStyle)
      .then((blob) => {
        if (cancelled) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setImage({ url: objectUrl, failed: false });
      })
      .catch(() => !cancelled && setImage({ url: null, failed: true }));

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [qrCode, format, style.foreground, style.background, style.logoStyle]);

  return image;
}

export function ImageBatchOptions({
  previewCode,
  onChange,
}: {
  previewCode?: QrCode;
  onChange: (choice: ImageBatchChoice) => void;
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState(DEFAULT_BATCH_CHOICE);
  const preview = usePreviewImage(previewCode, choice);

  function update(next: ImageBatchChoice) {
    setChoice(next);
    onChange(next);
  }

  return (
    <div className="d-flex flex-column gap-3">
      <fieldset>
        <legend className="qr-widget__micro">{t('dashboard.downloadFormat')}</legend>
        <div className="d-flex gap-3">
          {FORMATS.map((format) => (
            <div className="form-check" key={format}>
              <input
                id={`batch-format-${format}`}
                type="radio"
                name="batch-format"
                className="form-check-input"
                checked={choice.format === format}
                onChange={() => update({ ...choice, format })}
              />
              <label className="form-check-label" htmlFor={`batch-format-${format}`}>
                {t(`dashboard.format_${format}`)}
              </label>
            </div>
          ))}
        </div>
      </fieldset>

      <div className="batch-preview">
        {previewCode && (
          <p className="qr-widget__micro mb-1 text-truncate" title={previewCode.title}>
            {t('dashboard.previewOf', { title: previewCode.title })}
          </p>
        )}
        <QrArtwork
          idPrefix="batch"
          imageUrl={preview.url}
          alt={previewCode ? `QR code ${previewCode.title}` : ''}
          loadingLabel={preview.failed ? t('dashboard.previewFailed') : t('qrCode.rendering')}
          transparentHint={t('qrCode.backgroundTransparentHint')}
          style={choice.style}
          onStyleChange={(style) => update({ ...choice, style })}
        />
      </div>
    </div>
  );
}
